import React, { useState, useEffect } from 'react';
import {
  Form,
  setNestedObjectValues,
  useField,
  useFormikContext,
} from 'formik';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { ErrorM } from 'components/formComponents/ErrorM';
import { Input } from 'components/formComponents/Input';
import { format, parse } from 'date-fns';

type Props = {
  listData: any;
};

let filterOptList: any = [
  { value: 'contains', label: 'Contains' },
  { value: 'eq', label: 'Equals' },
];

export const TableFilters: React.FC<Props> = ({ listData }) => {
  const [filterOpts, setFilterOpts] = useState<any>();

  const formikContext = useFormikContext();

  useEffect(() => {
    if (listData) {
      let filterOptList = [];

      for (let i = 0; i < listData.columns.length; i++) {
        if (listData.columns[i].type === 'String') {
          filterOptList.push({
            column: listData.columns[i].columnKey,
            value: 'contains',
            label: 'Contains',
          });
        }
        if (listData.columns[i].type === 'DateTime') {
          filterOptList.push({
            column: listData.columns[i].columnKey,
            value: 'between',
            label: 'Between',
          });
        }
        if (listData.columns[i].bFilterable) {
          if (listData.columns[i].type === 'String') {
            formikContext.setFieldValue(
              listData.columns[i].columnKey + '_dropdown',
              { value: 'contains', label: 'Contains' }
            );
          }
          if (listData.columns[i].type === 'DateTime') {
            formikContext.setFieldValue(
              listData.columns[i].columnKey + '_dropdown',
              { value: 'between', label: 'Between' }
            );
          }
        }
      }
      setFilterOpts(filterOptList);
    }
  }, [listData]);

  return (
    <React.Fragment>
      {listData && (
        <Form>
          <div className="ListTableFilters">
            {listData &&
              listData.columns
                .filter((i: any) => i.bFilterable)
                .map((item: any, index: number) => {
                  if (item.bFilterable) {
                    if (item.type === 'String')
                      return (
                        <div
                          className="FormBox FormSideBySide left"
                          key={index}
                        >
                          <div className="FormLabel">
                            <label>{item.labelValue}</label>
                          </div>
                          <div className="FormField">
                            {filterOpts && (
                              <Select
                                key={'filter_dropdown_' + index}
                                isSearchable={false}
                                options={filterOptList}
                                value={filterOpts[index - 1]}
                                onChange={(value) => {
                                  formikContext.setFieldValue(
                                    item.columnKey + '_dropdown',
                                    value
                                  );
                                  let newArray = [...filterOpts];
                                  let newVal = Object.assign(
                                    { column: item.columnKey },
                                    value
                                  );
                                  newArray[index] = newVal;
                                  setFilterOpts(newArray);
                                }}
                              />
                            )}
                          </div>
                          <div className="FormField">
                            <Input
                              name={item.columnKey}
                              type="text"
                              onKeyUp={(e: any) =>
                                formikContext.setFieldValue(
                                  item.columnKey,
                                  e.target.value
                                )
                              }
                            />
                            <ErrorM name={item.columnKey} />
                          </div>
                        </div>
                      );
                    if (item.type === 'DateTime') {
                      return (
                        <DateTimeRangeField
                          name={item.columnKey}
                          label={item.labelValue}
                          setFilterOpts={setFilterOpts}
                          filterOpts={filterOpts}
                          index={index}
                        />
                      );
                    }
                  } else {
                    return null;
                  }
                })}
          </div>
        </Form>
      )}
    </React.Fragment>
  );
};

function DateTimeRangeField(props: {
  name: string;
  label: string;
  setFilterOpts: React.Dispatch<any>;
  filterOpts: any;
  index: number;
  range?: boolean;
}) {
  const [field, , helpers] = useField(props.name);

  const value = '';
  let values = value.split('TO');
  if (values.length !== 2) values = ['NULL', 'NULL'];

  const [startDate, setStartDate] = useState<any>(null);

  const [endDate, setEndDate] = useState<any>(null);

  function handleChangeFirst(date: Date | null) {
    const formattedDate = date ? format(date, 'MM/dd/yyyy') : 'NULL';

    let newArray = [...props.filterOpts];

    let newVal = {
      columnKey: props.name,
      operator: 'BETWEEN',
      value: `${formattedDate}TO${values[1]}`,
    };
    newArray[props.index] = newVal;
    props.setFilterOpts(newArray);

    helpers.setValue(
      `${formattedDate}TO${endDate ? format(endDate, 'MM/dd/yyyy') : 'NULL'}`
    );
  }

  function handleChangeSecond(date: Date | null) {
    const formattedDate = date ? format(date, 'MM/dd/yyyy') : 'NULL';

    let newArray = [...props.filterOpts];
    let newVal = {
      columnKey: props.name,
      operator: 'BETWEEN',
      value: `${values[0]}TO${formattedDate}}`,
    };
    newArray[props.index] = newVal;
    props.setFilterOpts(newArray);

    helpers.setValue(
      `${
        startDate ? format(startDate, 'MM/dd/yyyy') : 'NULL'
      }TO${formattedDate}`
    );
  }

  useEffect(() => {
    let newValues = field.value ? field.value.split('TO') : [];
    if (newValues.length !== 2) newValues = ['NULL', 'NULL'];
    setStartDate(() =>
      newValues[0] === 'NULL'
        ? null
        : parse(newValues[0], 'MM/dd/yyyy', new Date())
    );
    setEndDate(() =>
      newValues[1] === 'NULL'
        ? null
        : parse(newValues[1], 'MM/dd/yyyy', new Date())
    );
  }, [field.value]);

  return (
    <div className="FormBox FormSideBySide left" key={props.index}>
      <div className="FormLabel">
        <label>{props.label}</label>
      </div>
      <div className="FormField">
        <div className="DateFields">
          <DatePicker
            // locale={userLocale}
            className="EditBox"
            placeholderText="Starting at..."
            selected={startDate}
            onChange={handleChangeFirst}
            startDate={startDate}
            endDate={endDate}
            dateFormat="dd/MM/yyyy"
            popperModifiers={{
              hide: {
                enabled: false, // turn off since needs preventOverflow to be enabled
              },
            }}
          />
          <DatePicker
            // locale={userLocale}
            className="EditBox"
            placeholderText="Ending at..."
            selected={endDate}
            onChange={handleChangeSecond}
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            dateFormat="dd/MM/yyyy"
            popperModifiers={{
              hide: {
                enabled: false, // turn off since needs preventOverflow to be enabled
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}
